import {
  UserIcon,
  AddIcon,
  AdminPanelIcon,
  AppsIcon,
  DeviceIcon,
  EditIcon,
  EventIcon,
  ExportIcon,
  HomeIcon,
  ImportIcon,
  KeyIcon,
  LinkIcon,
  PendingIcon,
  PolicyIcon,
  PresetIcon,
  UpgradeIcon,
  SettingsIcon,
  StorageIcon,
  UsersIcon,
  VenueIcon,
  DashboardIcon,
  TutorialsIcon,
} from "components/ui/icons";
import { matchPath } from "react-router-dom";

interface BreadcrumbOverride {
  href?: string;
  label?: string;
  loading?: boolean;
}

type BreadcrumbOverrides = Record<string, BreadcrumbOverride>;
type BreadcrumbHref = string | ((params: Record<string, string>) => string);

export interface ResolvedBreadcrumbItem {
  href?: string;
  icon: typeof HomeIcon;
  isCurrentPage: boolean;
  label: string;
}

interface BreadcrumbItemConfig {
  dynamicKey?: string;
  href?: BreadcrumbHref;
  icon: typeof HomeIcon;
  label: string;
}

interface BreadcrumbRouteConfig {
  items: BreadcrumbItemConfig[];
  pattern: string;
}

const homeCrumb: BreadcrumbItemConfig = {
  href: "/",
  icon: HomeIcon,
  label: "Home",
};

const adminHomeCrumb: BreadcrumbItemConfig = {
  href: "/admin/home",
  icon: AdminPanelIcon,
  label: "Admin Home",
};

const breadcrumbRoutes: BreadcrumbRouteConfig[] = [
  // { pattern: "/", items: [homeCrumb] },
  { pattern: "/feed", items: [homeCrumb] },
  {
    pattern: "/events",
    items: [homeCrumb, { href: "/events", icon: EventIcon, label: "Events" }],
  },
  {
    pattern: "/events/create",
    items: [
      homeCrumb,
      { href: "/events", icon: EventIcon, label: "Events" },
      { icon: AddIcon, label: "Create" },
    ],
  },
  {
    pattern: "/events/:id",
    items: [
      homeCrumb,
      // Overridable so the event page can point it back at the list the user
      // came from. The href here is the fallback for arriving without that
      // history.
      {
        dynamicKey: "eventsList",
        href: "/events",
        icon: EventIcon,
        label: "Events",
      },
      {
        dynamicKey: "eventName",
        icon: EventIcon,
        label: "Event",
      },
    ],
  },
  {
    pattern: "/events/:id/edit",
    items: [
      homeCrumb,
      { href: "/events", icon: EventIcon, label: "Events" },
      {
        dynamicKey: "eventName",
        href: ({ id }) => `/events/${id}`,
        icon: EventIcon,
        label: "Event",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/events/:id/eventDevices/create",
    items: [
      homeCrumb,
      { href: "/events", icon: EventIcon, label: "Events" },
      {
        dynamicKey: "eventName",
        href: ({ id }) => `/events/${id}`,
        icon: EventIcon,
        label: "Event",
      },
      { icon: AddIcon, label: "Add Device" },
    ],
  },
  {
    pattern: "/devices",
    items: [
      homeCrumb,
      { href: "/devices", icon: DeviceIcon, label: "My Devices" },
    ],
  },
  {
    pattern: "/devices/create",
    items: [
      homeCrumb,
      { href: "/devices", icon: DeviceIcon, label: "My Devices" },
      { icon: AddIcon, label: "Create" },
    ],
  },
  {
    pattern: "/devices/:id",
    items: [
      homeCrumb,
      { href: "/devices", icon: DeviceIcon, label: "My Devices" },
      { dynamicKey: "deviceName", icon: DeviceIcon, label: "Device" },
    ],
  },
  {
    pattern: "/devices/:id/edit",
    items: [
      homeCrumb,
      { href: "/devices", icon: DeviceIcon, label: "My Devices" },
      {
        dynamicKey: "deviceName",
        href: ({ id }) => `/devices/${id}`,
        icon: DeviceIcon,
        label: "Device",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/tracks",
    items: [homeCrumb, { href: "/tracks", icon: VenueIcon, label: "Tracks" }],
  },
  {
    pattern: "/tracks/:venueId/events",
    items: [
      homeCrumb,
      // Overridable so the page can point it back at the list the user came
      // from - their search, region and page - rather than the default view.
      // The href here is the fallback for arriving without that history.
      {
        dynamicKey: "tracksList",
        href: "/tracks",
        icon: VenueIcon,
        label: "Tracks",
      },
      {
        dynamicKey: "venueName",
        icon: VenueIcon,
        label: "Track",
      },
      { icon: EventIcon, label: "Events" },
    ],
  },
  {
    pattern: "/eventdevice/:id",
    items: [
      homeCrumb,
      {
        dynamicKey: "eventName",
        icon: EventIcon,
        label: "Event",
      },
      {
        dynamicKey: "eventDeviceName",
        icon: DashboardIcon,
        label: "Event Device",
      },
    ],
  },
  {
    pattern: "/eventdevice/:id/edit",
    items: [
      homeCrumb,
      {
        dynamicKey: "eventDeviceName",
        href: ({ id }) => `/eventdevice/${id}`,
        icon: DeviceIcon,
        label: "Event Device",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/exports",
    items: [
      homeCrumb,
      { href: "/exports", icon: ExportIcon, label: "Exports" },
    ],
  },
  {
    pattern: "/imports",
    items: [
      homeCrumb,
      { href: "/imports", icon: ImportIcon, label: "Imports" },
    ],
  },
  {
    pattern: "/imports/create",
    items: [
      homeCrumb,
      { href: "/imports", icon: ImportIcon, label: "Imports" },
      { icon: AddIcon, label: "Create" },
    ],
  },
  {
    pattern: "/imports/:id",
    items: [
      homeCrumb,
      { href: "/imports", icon: ImportIcon, label: "Imports" },
      { icon: ImportIcon, label: "Import" },
    ],
  },
  {
    pattern: "/account",
    items: [homeCrumb, { icon: UserIcon, label: "Account" }],
  },
  {
    pattern: "/help",
    items: [
      homeCrumb,
      { href: "/help", icon: TutorialsIcon, label: "Tutorials" },
    ],
  },
  {
    pattern: "/plans",
    items: [homeCrumb, { icon: UpgradeIcon, label: "Plans" }],
  },
  {
    pattern: "/profile",
    items: [homeCrumb, { icon: UserIcon, label: "Profile" }],
  },
  {
    pattern: "/settings",
    items: [
      homeCrumb,
      { href: "/settings/general", icon: SettingsIcon, label: "Settings" },
    ],
  },
  {
    pattern: "/settings/general",
    items: [
      homeCrumb,
      { href: "/settings/general", icon: SettingsIcon, label: "Settings" },
      { icon: SettingsIcon, label: "General" },
    ],
  },
  {
    pattern: "/settings/api-keys",
    items: [
      homeCrumb,
      { href: "/settings/general", icon: SettingsIcon, label: "Settings" },
      { icon: KeyIcon, label: "API keys" },
    ],
  },
  {
    pattern: "/settings/oauth-applications",
    items: [
      homeCrumb,
      { href: "/settings/general", icon: SettingsIcon, label: "Settings" },
      { icon: AppsIcon, label: "OAuth applications" },
    ],
  },
  {
    pattern: "/settings/connected-apps",
    items: [
      homeCrumb,
      { href: "/settings/general", icon: SettingsIcon, label: "Settings" },
      { icon: LinkIcon, label: "Connected apps" },
    ],
  },
  {
    pattern: "/settings/preferences",
    items: [
      homeCrumb,
      { href: "/settings/general", icon: SettingsIcon, label: "Settings" },
      { icon: PresetIcon, label: "Preferences" },
    ],
  },
  {
    pattern: "/users/:slug",
    items: [
      homeCrumb,
      { dynamicKey: "userName", icon: UsersIcon, label: "User" },
    ],
  },
  {
    pattern: "/users/:slug/followers",
    items: [
      homeCrumb,
      {
        dynamicKey: "userName",
        href: ({ slug }) => `/users/${slug}`,
        icon: UsersIcon,
        label: "User",
      },
      { dynamicKey: "userConnections", icon: UsersIcon, label: "Followers" },
    ],
  },
  {
    pattern: "/users/:slug/following",
    items: [
      homeCrumb,
      {
        dynamicKey: "userName",
        href: ({ slug }) => `/users/${slug}`,
        icon: UsersIcon,
        label: "User",
      },
      { dynamicKey: "userConnections", icon: UsersIcon, label: "Following" },
    ],
  },
  {
    pattern: "/admin/home",
    items: [adminHomeCrumb],
  },
  {
    pattern: "/admin/policies",
    items: [
      adminHomeCrumb,
      { href: "/admin/policies", icon: PolicyIcon, label: "Privacy/ToS" },
    ],
  },
  {
    pattern: "/admin/policies/create",
    items: [
      adminHomeCrumb,
      { href: "/admin/policies", icon: PolicyIcon, label: "Privacy/ToS" },
      { icon: AddIcon, label: "New draft" },
    ],
  },
  {
    pattern: "/admin/policies/legal-entity",
    items: [
      adminHomeCrumb,
      { href: "/admin/policies", icon: PolicyIcon, label: "Privacy/ToS" },
      { icon: SettingsIcon, label: "Data controller" },
    ],
  },
  {
    pattern: "/admin/policies/:id/edit",
    items: [
      adminHomeCrumb,
      { href: "/admin/policies", icon: PolicyIcon, label: "Privacy/ToS" },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/admin/presets",
    items: [
      adminHomeCrumb,
      { href: "/admin/presets", icon: PresetIcon, label: "Presets" },
    ],
  },
  {
    pattern: "/admin/presets/create",
    items: [
      adminHomeCrumb,
      { href: "/admin/presets", icon: PresetIcon, label: "Presets" },
      { icon: AddIcon, label: "Create" },
    ],
  },
  {
    pattern: "/admin/presets/:id",
    items: [
      adminHomeCrumb,
      { href: "/admin/presets", icon: PresetIcon, label: "Presets" },
      { dynamicKey: "presetName", icon: PresetIcon, label: "Preset" },
    ],
  },
  {
    pattern: "/admin/presets/:id/edit",
    items: [
      adminHomeCrumb,
      { href: "/admin/presets", icon: PresetIcon, label: "Presets" },
      {
        dynamicKey: "presetName",
        href: ({ id }) => `/admin/presets/${id}`,
        icon: PresetIcon,
        label: "Preset",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/admin/presetTypes/create",
    items: [
      adminHomeCrumb,
      { href: "/admin/presets", icon: PresetIcon, label: "Presets" },
      { icon: AddIcon, label: "Create Type" },
    ],
  },
  {
    pattern: "/admin/presetTypes/:id",
    items: [
      adminHomeCrumb,
      { href: "/admin/presets", icon: PresetIcon, label: "Presets" },
      {
        dynamicKey: "presetTypeName",
        icon: PresetIcon,
        label: "Preset Type",
      },
    ],
  },
  {
    pattern: "/admin/presetTypes/:id/edit",
    items: [
      adminHomeCrumb,
      { href: "/admin/presets", icon: PresetIcon, label: "Presets" },
      {
        dynamicKey: "presetTypeName",
        href: ({ id }) => `/admin/presetTypes/${id}`,
        icon: PresetIcon,
        label: "Preset Type",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/admin/users",
    items: [
      adminHomeCrumb,
      { href: "/admin/users", icon: UsersIcon, label: "Users" },
    ],
  },
  {
    pattern: "/admin/users/create",
    items: [
      adminHomeCrumb,
      { href: "/admin/users", icon: UsersIcon, label: "Users" },
      { icon: AddIcon, label: "Create" },
    ],
  },
  {
    pattern: "/admin/users/:id",
    items: [
      adminHomeCrumb,
      { href: "/admin/users", icon: UsersIcon, label: "Users" },
      { dynamicKey: "userName", icon: UsersIcon, label: "User" },
    ],
  },
  {
    pattern: "/admin/users/:id/edit",
    items: [
      adminHomeCrumb,
      { href: "/admin/users", icon: UsersIcon, label: "Users" },
      {
        dynamicKey: "userName",
        href: ({ id }) => `/admin/users/${id}`,
        icon: UserIcon,
        label: "User",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/admin/users/:id/subscription",
    items: [
      adminHomeCrumb,
      { href: "/admin/users", icon: UsersIcon, label: "Users" },
      {
        dynamicKey: "userName",
        href: ({ id }) => `/admin/users/${id}`,
        icon: UserIcon,
        label: "User",
      },
      { icon: UpgradeIcon, label: "Subscription" },
    ],
  },
  {
    pattern: "/admin/accounts/:id",
    items: [
      adminHomeCrumb,
      { dynamicKey: "accountName", icon: UserIcon, label: "Account" },
    ],
  },
  {
    pattern: "/admin/venues",
    items: [
      adminHomeCrumb,
      { href: "/admin/venues", icon: VenueIcon, label: "Venues" },
    ],
  },
  {
    pattern: "/admin/venues/create",
    items: [
      adminHomeCrumb,
      { href: "/admin/venues", icon: VenueIcon, label: "Venues" },
      { icon: AddIcon, label: "Create" },
    ],
  },
  {
    pattern: "/admin/venues/:id",
    items: [
      adminHomeCrumb,
      { href: "/admin/venues", icon: VenueIcon, label: "Venues" },
      { dynamicKey: "venueName", icon: VenueIcon, label: "Venue" },
    ],
  },
  {
    pattern: "/admin/venues/:id/edit",
    items: [
      adminHomeCrumb,
      { href: "/admin/venues", icon: VenueIcon, label: "Venues" },
      {
        dynamicKey: "venueName",
        href: ({ id }) => `/admin/venues/${id}`,
        icon: VenueIcon,
        label: "Venue",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/admin/devices",
    items: [
      adminHomeCrumb,
      { href: "/admin/devices", icon: DeviceIcon, label: "Devices" },
    ],
  },
  {
    pattern: "/admin/devices/:id",
    items: [
      adminHomeCrumb,
      { href: "/admin/devices", icon: DeviceIcon, label: "Devices" },
      { dynamicKey: "deviceName", icon: DeviceIcon, label: "Device" },
    ],
  },
  {
    pattern: "/admin/events",
    items: [
      adminHomeCrumb,
      { href: "/admin/events", icon: EventIcon, label: "Events" },
    ],
  },
  {
    pattern: "/admin/events/:id/edit",
    items: [
      adminHomeCrumb,
      { href: "/admin/events", icon: EventIcon, label: "Events" },
      {
        dynamicKey: "eventName",
        href: ({ id }) => `/admin/events/${id}`,
        icon: EventIcon,
        label: "Event",
      },
      { icon: EditIcon, label: "Edit" },
    ],
  },
  {
    pattern: "/admin/events/:id",
    items: [
      adminHomeCrumb,
      { href: "/admin/events", icon: EventIcon, label: "Events" },
      { dynamicKey: "eventName", icon: EventIcon, label: "Event" },
    ],
  },
  {
    pattern: "/admin/eventDevices/:id",
    items: [
      adminHomeCrumb,
      {
        dynamicKey: "eventDeviceName",
        icon: DeviceIcon,
        label: "Event Device",
      },
    ],
  },
  {
    pattern: "/admin/eventDevices/:id/liveStream",
    items: [
      adminHomeCrumb,
      {
        dynamicKey: "eventDeviceName",
        href: ({ id }) => `/admin/eventDevices/${id}`,
        icon: DeviceIcon,
        label: "Event Device",
      },
      { icon: EventIcon, label: "Live Stream" },
    ],
  },
  {
    pattern: "/admin/imports",
    items: [
      adminHomeCrumb,
      { href: "/admin/imports", icon: ImportIcon, label: "Imports" },
    ],
  },
  {
    pattern: "/admin/imports/:id",
    items: [
      adminHomeCrumb,
      { href: "/admin/imports", icon: ImportIcon, label: "Imports" },
      { icon: ImportIcon, label: "Import" },
    ],
  },
  {
    pattern: "/admin/exports",
    items: [
      adminHomeCrumb,
      { href: "/admin/exports", icon: ExportIcon, label: "Exports" },
    ],
  },
  {
    pattern: "/admin/api-keys",
    items: [adminHomeCrumb, { icon: KeyIcon, label: "API keys" }],
  },
  {
    pattern: "/admin/oauth-applications",
    items: [adminHomeCrumb, { icon: AppsIcon, label: "OAuth applications" }],
  },
  {
    pattern: "/admin/processor-health",
    items: [adminHomeCrumb, { icon: StorageIcon, label: "Processor health" }],
  },
  {
    pattern: "/admin/eventDevices/:id/redis",
    items: [
      adminHomeCrumb,
      {
        href: "/admin/processor-health",
        icon: StorageIcon,
        label: "Processor health",
      },
      {
        dynamicKey: "eventDeviceName",
        href: ({ id }) => `/admin/eventDevices/${id}`,
        icon: DeviceIcon,
        label: "Event Device",
      },
      { icon: StorageIcon, label: "Redis and archiving" },
    ],
  },
];

export function resolveBreadcrumbs(
  pathname: string,
  overrides: BreadcrumbOverrides = {}
): ResolvedBreadcrumbItem[] {
  const matchedRoute = breadcrumbRoutes.find(({ pattern }) =>
    matchPath({ path: pattern, end: true }, pathname)
  );

  if (!matchedRoute) {
    return [];
  }

  const params =
    matchPath({ path: matchedRoute.pattern, end: true }, pathname)?.params ??
    {};

  return matchedRoute.items.map((item, index) => {
    const override = item.dynamicKey ? overrides[item.dynamicKey] : undefined;
    const isLoading = Boolean(override?.loading && !override?.label);

    return {
      href:
        index === matchedRoute.items.length - 1
          ? undefined
          : override?.href
          ? override.href
          : typeof item.href === "function"
          ? item.href(params as Record<string, string>)
          : item.href,
      icon: isLoading ? PendingIcon : item.icon,
      isCurrentPage: index === matchedRoute.items.length - 1,
      label: isLoading ? "" : override?.label || item.label,
    };
  });
}
